/*
 * Auto-add product
 * Keeps a configured product (e.g. cold-ship packaging) in the cart while the
 * conditions set in Theme settings > Auto-add product are met.
 *
 * Loaded synchronously in <head> so it wraps fetch/XHR before theme and app
 * scripts capture them. Every cart change (theme, Upcart, other apps) triggers
 * a reconcile against /cart.js once no other cart request is in flight, so our
 * write can't be overwritten by a concurrent one. Each write is verified with
 * a follow-up pass, and failures are retried.
 */
(function () {
  const config = window.autoAddProductConfig;
  if (!config || !config.variantId) return;

  const ROOT = (config.root || '/').replace(/\/$/, '');
  const OWN_HEADER = 'X-Auto-Add-Product';
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const STORAGE_KEY = 'autoAddProduct:state';
  const QUANTITY = 1; // The cart never holds more than one
  const triggerIds = (config.triggerProductIds || []).map(Number);
  const excludedIds = (config.excludedProductIds || []).map(Number);

  const SETTLE_DELAY = 300; // wait after the last cart request before reconciling
  const VERIFY_DELAY = 900; // re-check after our own write
  const RETRY_DELAY = 1200;
  const MAX_PASSES = 4; // consecutive writes/retries before giving up (e.g. fighting another app)
  const MAX_WAIT_IN_FLIGHT = 6000;

  let running = false;
  let queued = false;
  let timer = null;
  let inFlight = 0;
  let waitingSince = 0;
  let passes = 0;

  /* ---------- Watch cart requests ---------- */

  const isCartMutation = (url) => {
    try {
      return CART_MUTATION.test(new URL(url, window.location.origin).pathname);
    } catch (e) {
      return false;
    }
  };

  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    const request = nativeFetch.apply(window, arguments);
    let watch = false;
    try {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input && input.url;
      const headers = new Headers((init && init.headers) || (input instanceof Request ? input.headers : undefined));
      watch = !headers.has(OWN_HEADER) && !!url && isCartMutation(url);
    } catch (e) {}
    if (watch) {
      inFlight++;
      const done = () => {
        inFlight = Math.max(0, inFlight - 1);
        onCartChanged();
      };
      request.then(done, done);
    }
    return request;
  };

  const nativeOpen = XMLHttpRequest.prototype.open;
  const nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__autoAddWatch = !!url && isCartMutation(String(url));
    return nativeOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this.__autoAddWatch) {
      inFlight++;
      this.addEventListener('loadend', () => {
        inFlight = Math.max(0, inFlight - 1);
        onCartChanged();
      });
    }
    return nativeSend.apply(this, arguments);
  };

  /* ---------- Upcart hooks ---------- */

  // Upcart assigns these on init; wrap whatever it sets so we always hear about cart changes
  ['upcartOnAddToCart', 'upcartOnCartUpdated'].forEach((name) => {
    let handler = typeof window[name] === 'function' ? window[name] : null;
    try {
      Object.defineProperty(window, name, {
        configurable: true,
        get() {
          return function () {
            if (name === 'upcartOnAddToCart') onCartChanged();
            else schedule();
            return handler ? handler.apply(this, arguments) : undefined;
          };
        },
        set(value) {
          handler = typeof value === 'function' ? value : null;
        },
      });
    } catch (e) {}
  });

  /* ---------- State (only used when quantity is not locked) ---------- */

  const readState = () => {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
    } catch (e) {
      return {};
    }
  };

  const writeState = (state) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {}
  };

  /* ---------- Rules ---------- */

  const toCents = (amount) => {
    const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    return Math.round(Number(amount) * 100 * rate);
  };

  const qualifies = (otherItems) => {
    if (otherItems.length === 0 || !config.available) return false;

    // A cart holding only excluded products doesn't qualify; combined with anything else it does
    if (otherItems.every((item) => excludedIds.includes(item.product_id))) return false;

    const triggerItems =
      config.trigger === 'specific'
        ? otherItems.filter((item) => triggerIds.includes(item.product_id))
        : otherItems;
    if (triggerItems.length === 0) return false;

    if (!config.useThreshold) return true;

    const basisItems = config.thresholdBasis === 'trigger' ? triggerItems : otherItems;
    const total = basisItems.reduce((sum, item) => sum + item.final_line_price, 0);
    if (config.minTotal !== null && total < toCents(config.minTotal)) return false;
    if (config.maxTotal !== null && total > toCents(config.maxTotal)) return false;
    return true;
  };

  /* ---------- Reconcile ---------- */

  const cartRequest = async (path, body) => {
    // Don't let Upcart pop its drawer open for our background request
    const previous = window.upcartShouldOpenOnAddToCartBackgroundNetworkCall;
    window.upcartShouldOpenOnAddToCartBackgroundNetworkCall = false;
    try {
      const response = await window.fetch(`${ROOT}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', [OWN_HEADER]: '1' },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(`Auto-add product: ${path} failed (${response.status})`);
      return response.json();
    } finally {
      setTimeout(() => {
        if (previous === undefined) delete window.upcartShouldOpenOnAddToCartBackgroundNetworkCall;
        else window.upcartShouldOpenOnAddToCartBackgroundNetworkCall = previous;
      }, 500);
    }
  };

  const getCart = () =>
    window
      .fetch(`${ROOT}/cart.js?_=${Date.now()}`, {
        cache: 'no-store',
        headers: { Accept: 'application/json', [OWN_HEADER]: '1' },
      })
      .then((response) => {
        if (!response.ok) throw new Error(`Auto-add product: cart.js failed (${response.status})`);
        return response.json();
      });

  const setQuantities = (autoLines, quantity) => {
    if (autoLines.length === 0) {
      return cartRequest('/cart/add.js', { items: [{ id: config.variantId, quantity }] });
    }
    // Keep the first line at the target quantity, drop any duplicates
    const updates = {};
    autoLines.forEach((line, index) => {
      updates[line.key] = index === 0 ? quantity : 0;
    });
    return cartRequest('/cart/update.js', { updates });
  };

  // Returns true when it changed the cart
  async function reconcile() {
    const cart = await getCart();

    const autoLines = cart.items.filter((item) => item.product_id === config.productId);
    const otherItems = cart.items.filter((item) => item.product_id !== config.productId);
    const currentQty = autoLines.reduce((sum, item) => sum + item.quantity, 0);
    const shouldHave = qualifies(otherItems);
    const state = readState();

    // A fresh cart clears a previous "customer removed it" decision
    if (otherItems.length === 0 && state.declined) writeState({});

    let targetQty = currentQty;

    if (shouldHave) {
      if (config.lockQuantity) {
        targetQty = QUANTITY;
      } else if (state.addedQty && currentQty === 0) {
        // Customer removed the item themselves: respect it
        writeState({ declined: true, addedQty: 0 });
        return false;
      } else if (!state.declined || currentQty > 0) {
        targetQty = QUANTITY;
      }
    } else if (otherItems.length === 0 || config.removeWhenInvalid) {
      targetQty = 0;
    }

    const needsMerge = autoLines.length > 1;
    if (targetQty === currentQty && !needsMerge) return false;

    await setQuantities(autoLines, targetQty);
    if (!config.lockQuantity) writeState({ declined: false, addedQty: targetQty });

    // The cart page needs its empty state, which only a reload gives cleanly
    if (targetQty === 0 && otherItems.length === 0 && document.getElementById('main-cart-items')) {
      window.location.reload();
      return true;
    }
    await refreshCartUI();
    return true;
  }

  /* ---------- Refresh theme cart UI ---------- */

  const parseSection = (html, selector) => {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : null;
  };

  async function refreshCartUI() {
    const targets = [{ elementId: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }];

    const cartItems = document.getElementById('main-cart-items');
    const cartFooter = document.getElementById('main-cart-footer');
    if (cartItems && cartItems.dataset.id) {
      targets.push({ elementId: 'main-cart-items', section: cartItems.dataset.id, selector: '.js-contents' });
    }
    if (cartFooter && cartFooter.dataset.id) {
      targets.push({ elementId: 'main-cart-footer', section: cartFooter.dataset.id, selector: '.js-contents' });
    }

    try {
      const sections = await window
        .fetch(`${window.location.pathname}?sections=${targets.map((t) => t.section).join(',')}`, {
          headers: { [OWN_HEADER]: '1' },
        })
        .then((response) => response.json());

      targets.forEach((target) => {
        const container = document.getElementById(target.elementId);
        if (!container || !sections[target.section]) return;
        const html = parseSection(sections[target.section], target.selector);
        if (html === null) return;
        const destination = target.selector === '.js-contents' ? container.querySelector('.js-contents') : container;
        if (destination) destination.innerHTML = html;
      });
    } catch (e) {
      console.error(e);
    }

    // Upcart and other cart drawers
    if (typeof window.upcartRefreshCart === 'function') {
      try {
        await window.upcartRefreshCart();
      } catch (e) {}
    }
    document.dispatchEvent(new CustomEvent('auto-add-product:updated', { bubbles: true }));
  }

  /* ---------- Scheduling ---------- */

  async function run() {
    // Let other cart requests finish first so our write isn't overwritten
    if (inFlight > 0) {
      if (!waitingSince) waitingSince = Date.now();
      if (Date.now() - waitingSince < MAX_WAIT_IN_FLIGHT) {
        schedule(SETTLE_DELAY);
        return;
      }
    }
    waitingSince = 0;

    if (running) {
      queued = true;
      return;
    }
    running = true;
    let next = null;
    try {
      const changed = await reconcile();
      // Verify our write stuck (another request may have raced it)
      if (changed) next = VERIFY_DELAY;
      else passes = 0;
    } catch (e) {
      console.error(e);
      next = RETRY_DELAY;
    } finally {
      running = false;
    }

    if (queued) {
      queued = false;
      schedule();
    } else if (next !== null) {
      passes++;
      if (passes < MAX_PASSES) schedule(next);
      else {
        console.warn('Auto-add product: cart kept changing, stopped retrying. Is another app adding/removing the same product?');
        passes = 0;
      }
    }
  }

  function schedule(delay = SETTLE_DELAY) {
    clearTimeout(timer);
    timer = setTimeout(run, delay);
  }

  // A cart change made by the customer or an app starts a fresh set of passes
  function onCartChanged() {
    passes = 0;
    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => schedule());
  } else {
    schedule();
  }
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) schedule();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') schedule();
  });

  window.autoAddProduct = { refresh: onCartChanged };
})();
